// src/redux/slices/purchaseHistorySlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api from "api/api";
import endpoint from "api/endpoints";

/* ========== helpers ========== */

const normalizeOrder = (order) => {
  return {
    id: order.id || order.tranid,
    tranid: order.tranid,
    trandate: order.trandate,
    status: order.status || "Pending Fulfillment",
    transactionname: order.transactionname,
    trandisplayname: order.trandisplayname,
    shipcarrier: order.shipcarrier,
    memo: order.memo,
    total: order.total,
    subtotal: order.subtotal,
    // Add any other fields you need
    ...order,
  };
};

/* ========== Thunks ========== */

/**
 * Fetch purchase history transactions for a user
 */
export const fetchPurchaseHistory = createAsyncThunk(
  "purchaseHistory/fetchTransactions",
  async (
    { userId, limit, month, getAccessTokenSilently },
    { rejectWithValue }
  ) => {
    try {
      const token = await getAccessTokenSilently();

      // Build parameters object - only include limit if not "all"
      const params = { userId };
      if (limit !== "all") {
        params.limit = parseInt(limit, 10);
      }
      // "YYYY-MM" narrows the fetch server-side; "all" = the recent window.
      if (month && month !== "all") {
        params.month = month;
      }

      const url = endpoint.GET_TRANSACTION_BY_ID(params);
      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Response is { items, firstOrderDate }; tolerate the old bare-array
      // shape from a not-yet-redeployed backend.
      const data = res.data || {};
      const rawOrders = Array.isArray(data) ? data : data.items || [];
      return {
        orders: rawOrders.map(normalizeOrder),
        firstOrderDate: Array.isArray(data)
          ? null
          : data.firstOrderDate || null,
        month: month && month !== "all" ? month : "all",
      };
    } catch (err) {
      const status = err?.response?.status;
      const errorMessage =
        err?.response?.data?.error || "Failed to fetch purchase history";

      // Handle specific error types
      if (status === 403) {
        return rejectWithValue({
          type: "FORBIDDEN",
          message:
            "Access forbidden: You can only view your own purchase history",
          status: 403,
        });
      }

      if (status === 401) {
        return rejectWithValue({
          type: "UNAUTHORIZED",
          message: "Authentication required",
          status: 401,
        });
      }

      return rejectWithValue({
        type: "GENERAL_ERROR",
        message: errorMessage,
        status: status || 500,
      });
    }
  }
);

/**
 * Fetch transaction details by ID
 */
export const fetchTransactionDetails = createAsyncThunk(
  "purchaseHistory/fetchTransactionDetails",
  async (
    { transactionId, userId, getAccessTokenSilently },
    { rejectWithValue }
  ) => {
    try {
      const token = await getAccessTokenSilently();

      // You'll need to implement the actual endpoint for transaction details
      // This is a placeholder based on your existing structure
      const url = endpoint.GET_TRANSACTION_DETAILS({ transactionId, userId });
      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.error || "Failed to fetch transaction details"
      );
    }
  }
);

/* ========== Slice ========== */

const initialState = {
  // Data
  transactions: [],
  filteredTransactions: [],
  currentTransaction: null,
  // Which window `transactions` holds: "all" (recent window) or "YYYY-MM".
  loadedMonth: "all",
  // Customer's earliest transaction date — drives the full month dropdown.
  firstOrderDate: null,

  // Filter state
  filters: {
    fromDate: "",
    toDate: "",
    status: "All",
    sort: "recent",
    limit: "20",
  },

  // UI state
  activeOrderTypeTab: "SalesOrd",

  // Loading states
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed' | 'forbidden' | 'unauthorized'
  detailsStatus: "idle",
  error: null,
  detailsError: null,

  // Error tracking
  lastErrorType: null,
  authenticationFailed: false,

  // Metadata
  lastFetch: null,
  hasChangedLimit: false,
};

const purchaseHistorySlice = createSlice({
  name: "purchaseHistory",
  initialState,
  reducers: {
    // Filter actions
    setFromDate(state, action) {
      state.filters.fromDate = action.payload;
    },

    setToDate(state, action) {
      state.filters.toDate = action.payload;
    },

    setStatus(state, action) {
      state.filters.status = action.payload;
    },

    setSort(state, action) {
      state.filters.sort = action.payload;
    },

    setLimit(state, action) {
      const newLimit = action.payload;
      if (state.filters.limit !== newLimit) {
        state.filters.limit = newLimit;
        state.hasChangedLimit = true;
      }
    },

    // Set active order type tab
    setActiveOrderTypeTab(state, action) {
      state.activeOrderTypeTab = action.payload;
    },

    // Apply all filters at once
    applyAllFilters(state, action) {
      const { fromDate, toDate, status, sort, limit } = action.payload;

      // Update all filter values
      state.filters.fromDate = fromDate || "";
      state.filters.toDate = toDate || "";
      state.filters.status = status || "All";
      state.filters.sort = sort || "recent";

      // Handle limit change
      const newLimit = limit || 20;
      if (state.filters.limit !== newLimit) {
        state.filters.limit = newLimit;
        state.hasChangedLimit = true;
      }

      // Apply filtering logic
      if (!state.transactions.length) {
        state.filteredTransactions = [];
        return;
      }

      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;

      let filtered = state.transactions.filter((order) => {
        const d = new Date(order.trandate);
        if (isNaN(d)) return false;
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });

      if (status && status !== "All" && status !== "") {
        filtered = filtered.filter(
          (o) => (o.status || "Pending Fulfillment") === status
        );
      }

      filtered.sort((a, b) =>
        sort === "recent"
          ? new Date(b.trandate) - new Date(a.trandate)
          : new Date(a.trandate) - new Date(b.trandate)
      );

      state.filteredTransactions = filtered;
    },

    // Reset filters to default
    resetFilters(state) {
      state.filters = {
        fromDate: "",
        toDate: "",
        status: "All",
        sort: "recent",
        limit: "20",
      };
      state.activeOrderTypeTab = "SalesOrd"; // Reset tab to default
      state.hasChangedLimit = true; // Force refetch
    },

    // Clear all data and reset to initial state
    clearPurchaseHistory(state) {
      return initialState;
    },

    // Refetch on the next visit, keeping filters and the loaded month. Filing
    // a return adds an RMA pill to an order here, and without this it stayed
    // missing for the 5-minute staleness window.
    invalidatePurchaseHistory(state) {
      state.lastFetch = null;
    },

    // Set current transaction for details view
    setCurrentTransaction(state, action) {
      state.currentTransaction = action.payload;
    },

    // Clear errors
    clearErrors(state) {
      state.error = null;
      state.detailsError = null;
      state.lastErrorType = null;
      state.authenticationFailed = false;
    },

    // Reset authentication failure state
    resetAuthState(state) {
      state.authenticationFailed = false;
      state.lastErrorType = null;
      if (state.status === "forbidden" || state.status === "unauthorized") {
        state.status = "idle";
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch purchase history
      .addCase(fetchPurchaseHistory.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPurchaseHistory.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.transactions = action.payload.orders || [];
        state.loadedMonth = action.payload.month || "all";
        if (action.payload.firstOrderDate) {
          state.firstOrderDate = action.payload.firstOrderDate;
        }
        state.lastFetch = Date.now();
        state.hasChangedLimit = false;
        // Auto-apply current filters after successful fetch
        const { fromDate, toDate, status, sort, limit } = state.filters;
        purchaseHistorySlice.caseReducers.applyAllFilters(state, {
          payload: { fromDate, toDate, status, sort, limit },
        });
      })
      .addCase(fetchPurchaseHistory.rejected, (state, action) => {
        const errorPayload = action.payload;

        if (errorPayload?.type === "FORBIDDEN") {
          state.status = "forbidden";
          state.authenticationFailed = true;
          state.lastErrorType = "FORBIDDEN";
          state.error = errorPayload.message;
        } else if (errorPayload?.type === "UNAUTHORIZED") {
          state.status = "unauthorized";
          state.authenticationFailed = true;
          state.lastErrorType = "UNAUTHORIZED";
          state.error = errorPayload.message;
        } else {
          state.status = "failed";
          state.lastErrorType = "GENERAL_ERROR";
          state.error =
            errorPayload?.message || "Failed to fetch purchase history";
        }
      })

      // Fetch transaction details
      .addCase(fetchTransactionDetails.pending, (state) => {
        state.detailsStatus = "loading";
        state.detailsError = null;
      })
      .addCase(fetchTransactionDetails.fulfilled, (state, action) => {
        state.detailsStatus = "succeeded";
        state.currentTransaction = action.payload;
      })
      .addCase(fetchTransactionDetails.rejected, (state, action) => {
        state.detailsStatus = "failed";
        state.detailsError =
          action.payload || "Failed to fetch transaction details";
      });
  },
});

/* ========== Actions ========== */
export const {
  applyAllFilters,
  resetFilters,
  clearPurchaseHistory,
  invalidatePurchaseHistory,
  setCurrentTransaction,
  clearErrors,
  resetAuthState,
  setActiveOrderTypeTab,
} = purchaseHistorySlice.actions;

/* ========== Selectors ========== */
export const selectPurchaseHistory = (state) => state.purchaseHistory;
export const selectTransactions = (state) => state.purchaseHistory.transactions;
export const selectFilteredTransactions = (state) =>
  state.purchaseHistory.filteredTransactions;
export const selectCurrentTransaction = (state) =>
  state.purchaseHistory.currentTransaction;
export const selectFilters = (state) => state.purchaseHistory.filters;
export const selectActiveOrderTypeTab = (state) =>
  state.purchaseHistory.activeOrderTypeTab;
export const selectPurchaseHistoryStatus = (state) =>
  state.purchaseHistory.status;
export const selectPurchaseHistoryError = (state) =>
  state.purchaseHistory.error;
export const selectLoadedMonth = (state) => state.purchaseHistory.loadedMonth;
export const selectFirstOrderDate = (state) =>
  state.purchaseHistory.firstOrderDate;
export const selectDetailsStatus = (state) =>
  state.purchaseHistory.detailsStatus;
export const selectDetailsError = (state) => state.purchaseHistory.detailsError;

// Computed selectors
export const selectShouldFetch = (state) => {
  const { status, lastFetch, hasChangedLimit, authenticationFailed } =
    state.purchaseHistory;

  // Never refetch if authentication failed (403/401 errors)
  if (
    authenticationFailed ||
    status === "forbidden" ||
    status === "unauthorized"
  ) {
    return false;
  }

  // A failed fetch stays failed until the user does something: `lastFetch`
  // is only set on success, so without this a 500 or network error flipped
  // the "never fetched" branch back to true and PurchaseHistory's effect
  // re-dispatched the fetch forever (loading → failed → loading …). A limit
  // change is a user action, so that one branch still retries.
  if (status === "failed") return Boolean(hasChangedLimit);

  // Should fetch if:
  // 1. Never fetched and not currently loading — keyed on lastFetch, not
  //    transactions.length, so a legitimately empty result (e.g. a month with
  //    no orders) doesn't demand an endless refetch
  // 2. Limit has changed
  // 3. Data is older than 5 minutes (optional cache invalidation)
  const isStale = lastFetch && Date.now() - lastFetch > 5 * 60 * 1000; // 5 minutes

  return (!lastFetch && status !== "loading") || hasChangedLimit || isStale;
};

export const selectStatusOptions = (state) => {
  const transactions = selectTransactions(state);
  const statuses = [
    ...new Set(
      transactions.map((order) => order.status || "Pending Fulfillment")
    ),
  ];
  return [
    { value: "All", label: "All" },
    ...statuses.map((status) => ({ value: status, label: status })),
  ];
};

export default purchaseHistorySlice.reducer;
